import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextControl } from '@/module/Author/Text/TextControl';
import { ConferenceSelectControl } from '@/module/Nomen/Conference/ConferenceSelectControl';
import { ProceedingMembershipSelectControl } from '@/module/Nomen/ProceedingMembership/ProceedingMembershipSelectControl';

import { AuthorListControl, Content, Form, FormSubmit, TextInputControl } from '@/core/components';

import { ProceedingSchema } from './model/ProceedingSchema';

interface ProceedingFormProps extends FormSubmit<ProceedingSchema> {
  title: string;
  showWosFields: boolean;
  showScopusFields: boolean;
}

export function ProceedingForm(props: ProceedingFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit, showScopusFields, showWosFields } =
    props;
  const { t } = useTranslation();
  const form = useForm<ProceedingSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TextControl
              isSubmitting={isSubmitting}
              showWosFields={showWosFields}
              showScopusFields={showScopusFields}
            />
            <Group>
              <TextInputControl<ProceedingSchema>
                name="isbn.printCode"
                label={t('proceedings.form.fields.isbnPrint')}
                isSubmitting={isSubmitting}
              />
              <TextInputControl<ProceedingSchema>
                name="isbn.onlineCode"
                label={t('proceedings.form.fields.isbnOnline')}
                isSubmitting={isSubmitting}
              />
            </Group>
            <ConferenceSelectControl<ProceedingSchema>
              name="conference"
              label={t('proceedings.form.fields.conference')}
              isSubmitting={isSubmitting}
            />
            <TextInputControl<ProceedingSchema>
              name="bookName"
              label={t('proceedings.form.fields.bookName')}
              isSubmitting={isSubmitting}
            />
          </Content>
          <Content type="full">
            <AuthorListControl<ProceedingSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <ProceedingMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
